/**
 * Custom field dimension keys, and the split that routes them to their own payload key.
 *
 * Lives in utils rather than beside the reports pages because the store's params builder needs it
 * too, and a store slice may not import from `pages`.
 */

const KEY_PATTERN = /^cf_(\d+)$/;

/**
 * Keyed on the field's primary key, matching the backend. Not `token`, which a user can rename,
 * and not the slot the answer occupies, which is reused once a field is deleted.
 */
export const customFieldKey = (customFieldId: number | string) =>
  `cf_${customFieldId}`;

export const isCustomFieldKey = (key: unknown) =>
  KEY_PATTERN.test(String(key ?? ''));

export const customFieldIdFromKey = (key: unknown) => {
  const match = String(key ?? '').match(KEY_PATTERN);
  return match ? Number(match[1]) : null;
};

/**
 * The reports filter bar speaks `in`/`ex`; `custom_filter` speaks the vocabulary the rest of the
 * product already uses for custom fields. The comparison operators are identical on both sides,
 * so only the set operators translate.
 */
const CONDITION_TO_CUSTOM_FILTER: Record<string, string> = {
  in: 'is',
  ex: 'is_not',
};

const SCALAR_OPERATORS = ['eq', 'gt', 'gte', 'lt', 'lte'];

/**
 * Splits applied filters into the two payload keys the backend reads.
 *
 * Custom fields travel under `custom_filter`, keyed by the custom field id, because that is the
 * shape core already parses for them everywhere else. Built-in dimensions are untouched and keep
 * flowing into `filter_by`, so no existing report changes shape.
 *
 * A comparison operator carries one bound, but the filter bar stores every value as a list, so the
 * single value is unwrapped — the backend reads a number there, not an array.
 */
export const splitCustomFilters = (
  filters: Record<string, Record<string, unknown>> = {}
) => {
  const reportFilters: Record<string, unknown> = {};
  const customFilter: Record<string, Record<string, unknown>> = {};

  Object.entries(filters || {}).forEach(([key, constraints]) => {
    const customFieldId = customFieldIdFromKey(key);
    if (customFieldId === null) {
      reportFilters[key] = constraints;
      return;
    }

    const translated: Record<string, unknown> = {};
    Object.entries(constraints || {}).forEach(([condition, value]) => {
      const operator = CONDITION_TO_CUSTOM_FILTER[condition] ?? condition;
      translated[operator] =
        SCALAR_OPERATORS.includes(operator) && Array.isArray(value)
          ? value[0]
          : value;
    });

    if (Object.keys(translated).length) {
      customFilter[String(customFieldId)] = translated;
    }
  });

  return { reportFilters, customFilter };
};

const CUSTOM_FILTER_TO_CONDITION: Record<string, string> = {
  is: 'in',
  is_not: 'ex',
};

/**
 * The inverse of {@link splitCustomFilters}, for the surfaces that read a stored payload back
 * into the filter bar — the PDF print page hydrates redux from a saved `query`.
 *
 * Scalar bounds are re-wrapped in a list because that is how the filter bar holds every value;
 * splitCustomFilters unwraps them again on the way out.
 */
export const mergeCustomFilters = (
  reportFilters: Record<string, unknown> = {},
  customFilter: Record<string, Record<string, unknown>> = {}
) => {
  const merged: Record<string, unknown> = { ...reportFilters };

  Object.entries(customFilter || {}).forEach(([customFieldId, constraints]) => {
    const restored: Record<string, unknown> = {};

    Object.entries(constraints || {}).forEach(([operator, value]) => {
      const condition = CUSTOM_FILTER_TO_CONDITION[operator] ?? operator;
      restored[condition] = Array.isArray(value) ? value : [value];
    });

    merged[customFieldKey(customFieldId)] = restored;
  });

  return merged;
};

/**
 * Keeps only the custom field filters a report can actually answer.
 *
 * A custom report mixes blocks, and a filter belongs to an entity — an interviews block has no
 * requisition fields. Core rejects a field its report does not offer with `err_arg_unknown`, which
 * takes the whole block down with "Unable to Load Report"; dropping the inapplicable ones before
 * the request goes out is what turns that into the "not applicable to this block" note instead.
 *
 * `customFilter` is keyed by the plain custom field id (what {@link splitCustomFilters} emits);
 * `supportedByKey` is the `cf_<id>`-keyed catalog a report exposes (`useReportCustomFields().byKey`),
 * so the two are bridged through {@link customFieldKey}.
 */
export const applicableCustomFilter = (
  customFilter: Record<string, Record<string, unknown>> = {},
  supportedByKey: Record<string, unknown> = {}
): Record<string, Record<string, unknown>> =>
  Object.fromEntries(
    Object.entries(customFilter || {}).filter(
      ([customFieldId]) => supportedByKey[customFieldKey(customFieldId)]
    )
  );
